import { buildTerminologyMap, type TermKey, type TerminologyConfig, type TerminologyPluralConfig } from '@/modules/terminology/types';
import { pluralize } from '@/shared/lib/pluralize';

export type TermOptions = { plural?: boolean | number | null };
export type TermFn = (key: TermKey, options?: TermOptions) => string;
export type PlFn = (key: TermKey, n?: number | null) => string;
export type CountedFn = (key: TermKey, n: number) => string;
export type WithArticleFn = (key: TermKey) => string;
export type AgreeFn = <T>(n: number, singular: T, plural: T) => T;

function indefiniteArticle(phrase: string): 'a' | 'an' {
  return /^[aeiou]/i.test(phrase.trim()) ? 'an' : 'a';
}

function isPluralForm(p: boolean | number | null | undefined): boolean {
  if (p === undefined || p === false || p === null || p === 1) return false;
  return true;
}

export function buildTermHelpers(
  singularConfig: TerminologyConfig | null,
  pluralConfig: TerminologyPluralConfig,
  renamed: ReadonlySet<TermKey> = new Set(),
) {
  const singulars = buildTerminologyMap(singularConfig);
  const isRenamed = (key: TermKey) => renamed.has(key);

  const term: TermFn = (key, options) => {
    const singular = singulars[key];
    if (!isPluralForm(options?.plural)) return singular;
    return pluralize(singular, pluralConfig[key]);
  };

  const pl: PlFn = (key, n) =>
    term(key, { plural: n !== undefined ? n : true });

  const counted: CountedFn = (key, n) => `${n} ${pl(key, n)}`;
  const withArticle: WithArticleFn = (key) => {
    const noun = term(key);
    return `${indefiniteArticle(noun)} ${noun}`;
  };
  const agree: AgreeFn = (n, singular, plural) => n === 1 ? singular : plural;

  return { term, pl, counted, withArticle, agree, isRenamed };
}
